// --- Config defaults ---

export const DEFAULT_BATCH_SIZE = 10;
export const DEFAULT_BATCH_TIMEOUT_MS = 5000;
export const DEFAULT_MAX_QUEUE_SIZE = 100;
export const DEFAULT_MAX_RETRIES = 3;
export const DEFAULT_RETRY_BASE_DELAY_MS = 1000;
export const DEFAULT_RETRY_MAX_DELAY_MS = 30000;
export const DEFAULT_PERSISTENCE_DEBOUNCE_MS = 200;

// --- localStorage keys ---

export const ANONYMOUS_ID_STORAGE_KEY = 'league_anonymous_id';
export const EVENT_QUEUE_STORAGE_KEY = 'league_analytics_queue';

// --- Errors ---

export class AnalyticsAuthAbortError extends Error {
  constructor(readonly unsent: AnalyticsSdkEvent[]) {
    super('Analytics send aborted: login required');
  }
}

// --- Types & interfaces ---

export interface IdentityContext {
  anonymousId: string;
  userId?: string;
}

/**
 * The shape callers pass into `analyticsSdk.enqueue()`.
 * Identity fields and timestamp are attached by the SDK facade.
 */
export interface AnalyticsSdkEventInput {
  eventName: string;
  props: Record<string, unknown>;
}

/** Fully-enriched event stored in the queue and sent to transport. */
export interface AnalyticsSdkEvent {
  eventName: string;
  props: Record<string, unknown>;
}

export type EnqueuePosition = 'front' | 'back';

export interface IdentityManager {
  setUserId: (id: string) => void;
  clearUserId: () => void;
  getAnonymousId: () => string;
  getIdentityContext: () => IdentityContext;
}

export interface EventQueue {
  enqueue: (event: AnalyticsSdkEvent) => void;
  enqueueMany: (batch: AnalyticsSdkEvent[], position: EnqueuePosition) => void;
  getBatch: (count: number) => AnalyticsSdkEvent[];
  confirmSent: (count: number) => void;
  size: () => number;
  persistImmediately: () => void;
}

export interface Batcher {
  notify: () => void;
  flush: () => Promise<void>;
  destroy: () => void;
}

/**
 * Returns the subset of events that were **not** successfully sent.
 * An empty array means the full batch was delivered.
 *
 * @throws {AnalyticsAuthAbortError} when a login-required error is detected
 * mid-batch. The error carries the unsent tail; the retry policy catches this
 * and short-circuits without further retries or Sentry reporting.
 * Non-auth failures resolve with the unsent slice instead of throwing.
 */
export interface Transport {
  send: (batch: AnalyticsSdkEvent[]) => Promise<AnalyticsSdkEvent[]>;
}

/**
 * Wraps a Transport with exponential-backoff retry.
 * Resolves with any events that could not be delivered after all retries.
 */
export interface RetryPolicy {
  sendWithRetry: (batch: AnalyticsSdkEvent[]) => Promise<AnalyticsSdkEvent[]>;
}

export interface AnalyticsSdk {
  enqueue: (event: AnalyticsSdkEventInput) => void;
  setUserId: (id: string) => void;
  clearUserId: () => void;
  shutdown: () => void;
}
